import { FC, useContext, useMemo, useRef, useState } from 'react';

import { Button } from '@smartshell/ui-kit';
import cn from 'classnames';

import { useUploadFile } from 'hooks';
import { LocalizationContext } from 'localization';
import notification from 'services/notificationService';
import { ESettingsAliases, SettingsContext } from 'settings';

import { IMAGE_MAXIMUM_SIZE_MB, VIDEO_MAXIMUM_SIZE_MB } from './constants';
import { IScreenSaverProps } from './interfaces';
import s from './ScreenSaver.module.scss';
import { renderContent } from './utils';

export const ScreenSaver: FC<IScreenSaverProps> = ({
  disabled,
  fileFormat = '.jpeg, .jpg, .png, .webm, .mp4',
  group = 'primary',
  type = 'saver',
}) => {
  const { t } = useContext(LocalizationContext);
  const {
    state: {
      settings: { screenSaver, screenSmartKiosk, screenSmartTV },
    },
    actions: { setSetting },
  } = useContext(SettingsContext);

  const [loading, setLoading] = useState(false);

  const imgInput = useRef<HTMLInputElement>(null);

  const { uploadImage } = useUploadFile();

  const { file, alias } = useMemo<{
    file: string | null;
    alias: ESettingsAliases;
  }>(() => {
    switch (type) {
      case 'saver':
        return {
          file: screenSaver,
          alias: ESettingsAliases.SHELL_SCREENSAVER,
        };
      case 'smart_kiosk':
        return {
          file: screenSmartKiosk,
          alias: ESettingsAliases.SHELL_SMART_KIOSK,
        };
      case 'smart_tv':
        return {
          file: screenSmartTV,
          alias: ESettingsAliases.SHELL_SMART_TV,
        };
    }
  }, [type, screenSmartKiosk, screenSmartTV, screenSaver]);

  return (
    <div
      className={cn(s.imageContainer, disabled && s.disabled)}
      onClick={() => {
        if (loading) return;
        if (file) return;

        imgInput.current?.click();
      }}
    >
      {group !== 'none' && (
        <div className={cn(s.hostData, s[group])}>
          <span className={s.num}>1</span>
          {group === 'primary' && t('setting.shell.screensaver.group')}
        </div>
      )}
      {file && !loading && (
        <Button
          className={s.change}
          iconName="AddImageIcon"
          variant="primary"
          size="s"
          onClick={(e) => {
            e.stopPropagation();

            imgInput.current?.click();
          }}
        >
          {t('setting.shell.screensaver.change')}
        </Button>
      )}
      {renderContent(file, loading, t, fileFormat)}
      <input
        ref={imgInput}
        type="file"
        accept={fileFormat}
        className={s.input}
        value=""
        onChange={async (event) => {
          const file = event.target.files?.[0];

          if (!file) return;

          const type = file.type.split('/')[0];

          if (
            type === 'image' &&
            file.size > IMAGE_MAXIMUM_SIZE_MB * 1024 * 1024
          ) {
            return notification.info({
              t,
              type: 'warning',
              title: t('errors.upload.size'),
              message: t('errors.upload.size.description', 2),
            });
          }

          if (
            type === 'video' &&
            file.size > VIDEO_MAXIMUM_SIZE_MB * 1024 * 1024
          ) {
            return notification.info({
              t,
              type: 'warning',
              title: t('errors.upload.size'),
              message: t('errors.upload.size.description', 30),
            });
          }

          setLoading(true);
          try {
            const value = await uploadImage(file);
            setSetting({ alias, value });
          } catch (error: unknown) {
            notification.info({
              t,
              type: 'error',
              title: t('upload.file.error'),
              message: error instanceof Error ? error.message : undefined,
            });
          } finally {
            setLoading(false);
          }
        }}
      />
    </div>
  );
};
